import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CrowdLogoFull } from "@/components/CrowdLogo";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Preencha todos os campos");
      return;
    }
    
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Login realizado com sucesso!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Erro ao fazer login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Hero with Crowd SA branding */}
      <div className="hidden lg:flex flex-1 hero-gradient flex-col justify-between p-12 text-white">
        <CrowdLogoFull className="text-white" />
        
        <div className="space-y-6">
          <h1 className="text-5xl font-bold heading-font leading-tight">
            A soma que<br />
            <span className="text-[#00A7E1]">multiplica</span>
          </h1>
          <p className="text-lg text-white/70 max-w-md leading-relaxed">
            Organize seus projetos com inteligência artificial. 
            Gerencie clientes, tarefas e acompanhe o progresso em tempo real.
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex -space-x-3">
            <div className="w-10 h-10 rounded-full bg-[#00A7E1] flex items-center justify-center text-[#0A1628] font-bold text-sm border-2 border-[#0A1628]">FT</div>
            <div className="w-10 h-10 rounded-full bg-[#3BA2A6] flex items-center justify-center text-white font-bold text-sm border-2 border-[#0A1628]">DT</div>
            <div className="w-10 h-10 rounded-full bg-[#22C55E] flex items-center justify-center text-white font-bold text-sm border-2 border-[#0A1628]">+5</div>
          </div>
          <p className="text-sm text-white/60">Equipe Crowd gerenciando projetos</p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <Card className="w-full max-w-md border-0 shadow-none lg:border lg:shadow-lg lg:rounded-2xl">
          <CardHeader className="space-y-4 text-center pb-2">
            <div className="lg:hidden flex items-center justify-center mb-4">
              <CrowdLogoFull />
            </div>
            <div>
              <CardTitle className="text-2xl heading-font">Bem-vindo de volta</CardTitle>
              <CardDescription className="mt-2">
                Entre com suas credenciais para acessar o Board
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="h-12 rounded-lg border-border/50 focus:border-[#00A7E1] focus:ring-[#00A7E1]/20"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">Senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="h-12 rounded-lg pr-12 border-border/50 focus:border-[#00A7E1] focus:ring-[#00A7E1]/20"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1 h-10 w-10 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                  </Button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-[#00A7E1] hover:bg-[#0095c8] text-[#0A1628] font-semibold rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-cyan-500/20 hover:-translate-y-0.5"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? "Entrando..." : "Entrar"}
              </Button>
            </form>
            <div className="mt-8 text-center text-sm">
              <span className="text-muted-foreground">Não tem uma conta? </span>
              <Link to="/register" className="text-[#00A7E1] hover:underline font-semibold" data-testid="register-link">
                Criar conta
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
